import { useEffect, useState } from 'react';
import { useQueryClient } from '@tanstack/react-query';
import { useTranslation } from 'react-i18next';
import { CircleAlert } from 'lucide-react';
import { Link } from 'react-router';
import { PaginaSecao } from '../ui/PaginaSecao';
import { Vazio } from '../ui/Estados';
import { useSessao } from '../sessao';
import { MODULOS_WEB } from '../modulos';
import { menuVisivel } from '../layout/menu';

/** F10: indicadores dos módulos instalados e atalhos para as seções. */
export function Inicio() {
  const { t } = useTranslation();
  const eu = useSessao();
  const instalados = eu.modulos ?? [];
  const comIndicadores = MODULOS_WEB.filter((m) => m.Indicadores && instalados.includes(m.slug));
  const atalhos = menuVisivel(instalados, eu.permissoes).filter((m) => m.chave !== 'inicio');
  const falhas = useFalhasDosIndicadores(instalados);
  const pode = (p: string) => eu.permissoes.includes('*') || eu.permissoes.includes(p) || eu.permissoes.includes(`${p.split('.')[0]}.*`);
  const acao = MODULOS_WEB.filter((m) => m.acaoInicio && instalados.includes(m.slug) && pode(m.acaoInicio.permissao)).sort((a, b) => (a.acaoInicio!.ordem ?? 50) - (b.acaoInicio!.ordem ?? 50))[0];
  const qc = useQueryClient();
  return (
    <PaginaSecao
      kicker={t('inicio.kicker')}
      titulo={t('inicio.titulo')}
      acao={
        acao?.acaoInicio && (
          <Link to={acao.acaoInicio.rota} className="inline-flex h-10 items-center rounded-lg bg-acento px-4 text-sm font-medium text-acento-tinta hover:bg-acento-forte">
            {t(acao.acaoInicio.rotulo, { ns: acao.slug })}
          </Link>
        )
      }
    >
      {falhas > 0 && (
        <div role="alert" className="mb-4 flex flex-wrap items-center gap-3 rounded-xl border border-perigo/40 bg-perigo/5 px-4 py-3 text-sm">
          <CircleAlert size={18} strokeWidth={1.75} className="text-perigo" />
          <span className="min-w-0 flex-1">{t('inicio.erroIndicadores')}</span>
          <button type="button" onClick={() => void qc.refetchQueries({ type: 'active', predicate: (q) => q.state.status === 'error' })} className="h-8 rounded-lg border border-borda px-3 hover:bg-sup-2">
            {t('estado.tentar')}
          </button>
        </div>
      )}
      {comIndicadores.length === 0 ? (
        <Vazio titulo={t('inicio.vazioTitulo')} texto={t('inicio.vazioTexto')} />
      ) : (
        <div className="grid gap-4 sm:grid-cols-2 xl:grid-cols-4">
          {comIndicadores.map((m) => {
            const I = m.Indicadores!;
            return <I key={m.slug} />;
          })}
        </div>
      )}
      {atalhos.length > 0 && (
        <nav aria-label={t('inicio.atalhos')} className="mt-8 flex flex-wrap gap-2">
          {atalhos.map((a) => (
            <Link key={a.chave} to={a.rota} className="inline-flex items-center gap-2 rounded-lg border border-borda px-3 py-2 text-sm text-tinta-2 hover:bg-sup-1 hover:text-tinta-1">
              <a.icone size={15} strokeWidth={1.75} /> {t(`menu.${a.chave}`)}
            </Link>
          ))}
        </nav>
      )}
    </PaginaSecao>
  );
}

/**
 * F10 e R3: os indicadores são consultas dos módulos (chave começa pelo slug). Se alguma falha, o Início avisa e
 * oferece tentar de novo, em vez de deixar os cartões carregando para sempre.
 */
function useFalhasDosIndicadores(instalados: readonly string[]): number {
  const qc = useQueryClient();
  const contar = () => qc.getQueryCache().findAll({ predicate: (q) => q.state.status === 'error' && instalados.includes(String(q.queryKey[0])) }).length;
  const [n, setN] = useState(contar);
  useEffect(() => qc.getQueryCache().subscribe(() => setN(contar())), [qc, instalados]); // eslint-disable-line react-hooks/exhaustive-deps
  return n;
}
